// ChoreographyPhaseStrip.tsx — FE_SPEC §3.X.3.12 (NEW · M2 replaces M0 PhaseStrip).
//
// Renders one chip per Composer-emitted phase (3..7 per Validator I5).
// Highlights the active phase. Renders an iron_tone glyph for
// 'compassionate_critique' phases. budget_sec on title tooltip.
//
// 4 substates per PR-FE-SPEC-02:
//   default: full strip
//   loading: phases=[] AND no choreography yet → skeleton (5 placeholder dots)
//   empty:   phases=[] AND not loading → return null (no choreography emitted)
//   error:   current_phase_idx OOR → render w/o highlight + console.warn
//
// Source: docs/contracts/m2/dto.m2.schema.json#/$defs/ChoreographyPhase.

import { useTranslation } from 'react-i18next';
import type { ChoreographyPhase } from '@/api/m0_sessions';
import { PHASE_NAMES } from '@/ui/phases/PhaseProps';

export interface PhaseStep extends ChoreographyPhase {
  // alias for FE_SPEC clarity
}

interface ChoreographyPhaseStripProps {
  phases: PhaseStep[];
  current_phase_idx: number | null;
  /** Optional totals (BE phase_paused payload). */
  phases_completed?: number | null;
  phases_total?: number | null;
  /** When true, even with phases=[] render a 5-chip skeleton. */
  loading?: boolean;
}

export default function ChoreographyPhaseStrip({
  phases,
  current_phase_idx,
  phases_completed,
  phases_total,
  loading = false,
}: ChoreographyPhaseStripProps) {
  const { t } = useTranslation();

  // loading substate
  if ((!phases || phases.length === 0) && loading) {
    return (
      <div
        data-testid="choreography-phase-strip"
        data-state="loading"
        aria-busy="true"
        className="flex flex-wrap gap-2"
      >
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="px-3 py-1 rounded text-xs bg-paper-2 text-ink-3 animate-pulse w-20 h-6"
          />
        ))}
      </div>
    );
  }

  // empty substate — no choreography
  if (!phases || phases.length === 0) {
    return null;
  }

  // error substate — current_phase_idx out of range (defensive)
  const idxOOR =
    current_phase_idx != null &&
    (current_phase_idx < 0 || current_phase_idx >= phases.length);
  if (idxOOR) {
    // eslint-disable-next-line no-console
    console.warn('choreography_phase_strip_idx_oor', {
      current_phase_idx,
      phases_len: phases.length,
    });
  }

  const validIdx = idxOOR ? null : current_phase_idx;

  return (
    <ol
      role="list"
      data-testid="choreography-phase-strip"
      data-state={idxOOR ? 'error' : 'default'}
      data-phases-completed={phases_completed ?? ''}
      data-phases-total={phases_total ?? ''}
      aria-label={t('phaseStrip.aria', { defaultValue: 'Council phase' })}
      className="flex flex-wrap gap-2"
    >
      {phases.map((phase, i) => {
        const isCurrent = validIdx != null && i === validIdx;
        const isPast = validIdx != null && i < validIdx;
        const known = (PHASE_NAMES as readonly string[]).includes(phase.name);
        const cls = isCurrent
          ? 'bg-ink text-paper font-semibold'
          : isPast
            ? 'bg-paper-2 text-ink-3'
            : 'bg-paper-2 text-ink-2 opacity-60';
        return (
          <li
            key={`${phase.name}-${i}`}
            data-phase-idx={i}
            data-phase-name={phase.name}
            data-phase-current={isCurrent ? 'true' : 'false'}
            aria-current={isCurrent ? 'step' : undefined}
            title={`${t(`phase.${phase.name}`, { defaultValue: phase.name })} · ${phase.budget_sec}s`}
            className={`px-3 py-1 rounded text-xs ${cls} ${!known ? 'border border-vermilion-300' : ''}`}
          >
            <span className="font-mono mr-1">P{i + 1}</span>
            <span>{t(`phase.${phase.name}`, { defaultValue: phase.name })}</span>
            {phase.iron_tone === 'compassionate_critique' && (
              <span className="ml-1" aria-hidden>
                ♡
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
